import React, { useEffect, useState } from 'react';
import { PackageOpen, AlertCircle, ExternalLink } from 'lucide-react';
import { getPlayerProfile } from '../../../../services/firestoreService';
import { db } from '../../../../config/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { SizeSpacingPanel, ColorsFontsPanel } from '../common/BlockStyleSettings';
import { BLOCK_FEATURES } from '../common/commonDefaults';

/**
 * SingleProductEditor - Editor for the single-product block
 * Fetches user's active products and lets them pick one to showcase
 */
const SingleProductEditor = ({ block, onChange, profileId, theme, hideStylePanels }) => {
  const { content } = block;
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadProducts = async () => {
      if (!profileId) {
        setLoading(false);
        return;
      }

      try {
        const profile = await getPlayerProfile(profileId);
        const vendorId = profile?.userId || profileId;

        const q = query(
          collection(db, 'products'),
          where('vendorId', '==', vendorId),
          where('isActive', '==', true)
        );
        const snapshot = await getDocs(q);
        const productsData = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setProducts(productsData);
      } catch (err) {
        console.error('Error loading products:', err);
        setError('Failed to load products');
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, [profileId]);

  const handleChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleStyleChange = (styleUpdates) => {
    onChange({ style: { ...(content.style || {}), ...styleUpdates } });
  };

  const selectedProduct = products.find((p) => p.id === content.productId);

  // Helper to get best product image
  const getProductImage = (product) => {
    if (product.product_type === 'pod' && product.variants?.length > 0) {
      const firstVariant = product.variants[0];
      if (firstVariant.mockup_images && firstVariant.variant_map) {
        const blackVariantKey = Object.keys(firstVariant.variant_map).find((key) =>
          key.startsWith('black_')
        );
        if (blackVariantKey) {
          const blackPrintifyVariantId = firstVariant.variant_map[blackVariantKey];
          const blackMockup = firstVariant.mockup_images.find(
            (img) => img.variant_ids && img.variant_ids.includes(blackPrintifyVariantId)
          );
          if (blackMockup?.src) return blackMockup.src;
        }
      }
      return product.preview_image || firstVariant?.mockup_images_urls?.[0] || null;
    }
    return product.preview_image || product.imageUrl || null;
  };

  const formatPrice = (product) => {
    if (product.product_type === 'pod' && product.variants?.length > 0) {
      const prices = product.variants.map((v) => v.price).filter(Boolean);
      if (prices.length > 0) {
        const min = Math.min(...prices);
        return `From $${(min / 100).toFixed(2)}`;
      }
    }
    if (product.price) return `$${(product.price / 100).toFixed(2)}`;
    return '';
  };

  return (
    <div className="space-y-4">
      {/* Section Heading */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Section Heading
        </label>
        <input
          type="text"
          value={content.heading || ''}
          onChange={(e) => handleChange('heading', e.target.value)}
          placeholder="Featured Product"
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
        />
      </div>

      {/* Product Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Select Product
        </label>
        {loading ? (
          <div className="flex items-center gap-2 py-3 text-gray-400 text-sm">
            <div className="w-4 h-4 border-2 border-gray-600 border-t-[#749555] rounded-full animate-spin" />
            Loading products...
          </div>
        ) : error ? (
          <p className="text-red-400 text-sm flex items-center gap-1">
            <AlertCircle size={14} />
            {error}
          </p>
        ) : products.length === 0 ? (
          <div className="text-center py-6 bg-[#1a1a2e] border border-gray-700 rounded-lg">
            <PackageOpen size={28} className="mx-auto text-gray-600 mb-2" />
            <p className="text-gray-500 text-sm">No active products</p>
            <p className="text-gray-600 text-xs">Add products in your Shop to use this block</p>
          </div>
        ) : (
          <select
            value={content.productId || ''}
            onChange={(e) => handleChange('productId', e.target.value)}
            className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white focus:border-[#749555] focus:outline-none"
          >
            <option value="">Choose a product...</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title} — {formatPrice(p)}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Selected Product Preview */}
      {selectedProduct && (
        <div className="flex items-center gap-3 p-3 bg-[#749555]/10 border border-[#749555]/30 rounded-lg">
          {getProductImage(selectedProduct) ? (
            <img
              src={getProductImage(selectedProduct)}
              alt={selectedProduct.title}
              className="w-14 h-14 object-cover rounded flex-shrink-0"
            />
          ) : (
            <div className="w-14 h-14 bg-gray-800 rounded flex items-center justify-center flex-shrink-0">
              <PackageOpen size={20} className="text-gray-500" />
            </div>
          )}
          <div className="min-w-0">
            <p className="text-sm text-white font-medium truncate">{selectedProduct.title}</p>
            <p className="text-xs text-[#749555]">{formatPrice(selectedProduct)}</p>
            {selectedProduct.product_type === 'pod' && (
              <p className="text-xs text-gray-500">POD — links to product page</p>
            )}
          </div>
        </div>
      )}

      {/* Button Text */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Button Text
        </label>
        <input
          type="text"
          value={content.buttonText || 'Buy Now'}
          onChange={(e) => handleChange('buttonText', e.target.value)}
          placeholder="Buy Now"
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
        />
      </div>

      {/* Layout */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Layout
        </label>
        <div className="flex gap-2">
          {[
            { value: 'imageLeft', label: 'Image Left' },
            { value: 'imageRight', label: 'Image Right' },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => handleChange('layout', option.value)}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                content.layout === option.value
                  ? 'bg-[#749555] text-white'
                  : 'bg-[#1a1a2e] text-gray-400 hover:text-white border border-gray-700'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Show Description */}
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={content.showDescription !== false}
          onChange={(e) => handleChange('showDescription', e.target.checked)}
          className="w-4 h-4 rounded border-gray-600 bg-[#1a1a2e] text-[#749555] focus:ring-[#749555]"
        />
        <span className="text-sm text-gray-300">Show Description</span>
      </label>

      {/* Style Panels */}
      {!hideStylePanels && (<>
      <SizeSpacingPanel
        style={content.style || {}}
        onChange={handleStyleChange}
        features={BLOCK_FEATURES['single-product']}
      />
      <ColorsFontsPanel
        style={content.style || {}}
        onChange={handleStyleChange}
        features={BLOCK_FEATURES['single-product']}
        theme={theme || {}}
        blockType="single-product"
      />
      </>)}
      <a
        href="/dashboard/website/shop"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 w-full py-2.5 bg-[#749555] hover:bg-[#8bab6a] text-white rounded-lg transition-colors text-sm font-medium"
      >
        Manage Shop
        <ExternalLink size={14} />
      </a>
    </div>
  );
};

export default SingleProductEditor;
